// frontend/src/components/AdminTeamDetail/PlayerCard.js
import React, { useState, useRef, useEffect } from 'react';
import HighlightedText from '../HighlightedText';
import { formatRelativeTime } from '../../utils/dateUtils';

const PlayerCard = ({
    player,
    isSelected,
    isSuperAdmin,          // Only super_admins may change the Confirmed flag
    onToggleConfirmed,     // Toggle player.confirmed (super_admin only)
    onToggleSelection,
    onTogglePhoto,
    onOpenLightbox,
    onDownload,
    onReplacePhoto,
    onDeletePhoto,
    onDeletePlayer,
    onOpenLink,
    onSyncToSportradar,
    onViewSportradarPhoto,
    onEditName,
    onEditPlayerDetails,  // Save display name + manual First/Last name together
    onRestorePhoto,  // Restore original uploaded photo (from uploaded_photo_url)
    onRestoreSportradarPhoto,  // Restore Sportradar photo (re-download)
    onRemoveBackground,  // Remove background from current photo
    onCropPhoto,  // Crop the current photo
    onRestoreOriginalPhoto,  // Restore pre-background-removal photo (from original_photo_url)
    isDuplicate,  // True if another player in the roster has a very similar name
    searchQuery   // Text from search bar to highlight
}) => {
    const [isEditing, setIsEditing] = useState(false);
    const [editedName, setEditedName] = useState(player.player_name);
    const [editedFirst, setEditedFirst] = useState(player.first_name || '');
    const [editedLast, setEditedLast] = useState(player.last_name || '');
    const [menuOpen, setMenuOpen] = useState(false);
    const menuRef = useRef(null);

    // Close menu when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (menuRef.current && !menuRef.current.contains(event.target)) {
                setMenuOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Open the editor, seeding fields from the current player values.
    const openEdit = () => {
        setEditedName(player.player_name);
        setEditedFirst(player.first_name || '');
        setEditedLast(player.last_name || '');
        setIsEditing(true);
    };

    const cancelEdit = () => {
        setEditedName(player.player_name);
        setEditedFirst(player.first_name || '');
        setEditedLast(player.last_name || '');
        setIsEditing(false);
    };

    const handleSaveName = () => {
        const trimmed = editedName.trim();
        if (trimmed) {
            if (onEditPlayerDetails) {
                // Save display name + first/last together
                onEditPlayerDetails(player, {
                    player_name: trimmed,
                    first_name: editedFirst,
                    last_name: editedLast,
                });
            } else if (trimmed !== player.player_name) {
                // Fallback for callers that only wired the rename handler
                onEditName(player, trimmed);
            }
        }
        setIsEditing(false);
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            handleSaveName();
        } else if (e.key === 'Escape') {
            cancelEdit();
        }
    };

    const handleMenuAction = (action) => {
        setMenuOpen(false);
        action();
    };

    // Check if uploaded photo can be restored (has backup and current photo is different)
    const canRestoreUploadedPhoto = player.uploaded_photo_url &&
        player.photo_url !== player.uploaded_photo_url;

    // Check if Sportradar photo can be restored (linked player with SR photo URL)
    const canRestoreSportradarPhoto = player.person_id && player.sportradar_photo_url &&
        !player.photo_url?.includes('_SR_'); // Only if not already using SR photo

    // Check if original photo can be restored (pre-background-removal backup exists and differs from current)
    const canRestoreOriginalPhoto = player.original_photo_url &&
        player.photo_url !== player.original_photo_url;

    return (
        <div className="relative group/card h-full flex flex-col">
            {/* Dynamic Color Glow (Wow Factor) */}
            {player.upload_status && player.photo_url && (
                <div 
                    className="absolute -inset-3 bg-cover bg-center blur-xl opacity-0 group-hover/card:opacity-100 group-hover/card:scale-105 transition-all duration-700 rounded-2xl pointer-events-none z-0"
                    style={{ backgroundImage: `url(${player.photo_url})` }}
                />
            )}
            
            {/* Main Card */}
            <div className={`relative z-10 h-full flex flex-col bg-white rounded-lg shadow-sm border overflow-hidden hover:shadow-md transition-all ${isSelected ? 'border-mjwc-primary ring-2 ring-mjwc-primary ring-opacity-50' : 'border-gray-200'}`}>
                <div className="relative">
                {/* Selection Checkbox Overlay */}
                <div className="absolute top-2 left-2 z-10">
                    <input
                        type="checkbox"
                        className="w-5 h-5 rounded border-gray-300 text-mjwc-primary focus:ring-mjwc-primary cursor-pointer"
                        checked={isSelected}
                        onChange={(e) => {
                            e.stopPropagation();
                            onToggleSelection(player.id);
                        }}
                    />
                </div>

                {/* SR Badge for linked players */}
                {player.person_id && (
                    <div className="absolute top-2 right-2 z-10 bg-indigo-600 text-white text-xs font-bold px-1.5 py-0.5 rounded">
                        SR
                    </div>
                )}

                {/* Duplicate Name Warning Badge */}
                {isDuplicate && (
                    <div
                        className="absolute top-8 left-2 z-10 bg-amber-400 text-white rounded-full p-0.5 shadow"
                        title="Warning: Another player in this roster has a very similar name. Please verify this is not a duplicate."
                    >
                        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                        </svg>
                    </div>
                )}

                {/* Toggle Button Overlay (Original/No-BG) */}
                {player.original_photo_url && player.nobg_photo_url && (
                    <div className="absolute top-2 right-10 z-10">
                        <button
                            onClick={(e) => { e.stopPropagation(); onTogglePhoto(player); }}
                            className="bg-white/90 text-gray-700 p-1.5 rounded-full shadow hover:bg-white hover:text-mjwc-primary text-xs font-bold transition-all border border-gray-200 active:scale-95"
                            title={player.photo_url === player.nobg_photo_url ? "Show Original" : "Show No-BG"}
                        >
                            {player.photo_url === player.nobg_photo_url ? (
                                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                </svg>
                            ) : (
                                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.384-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
                                </svg>
                            )}
                        </button>
                    </div>
                )}

                {player.upload_status && player.photo_url ? (
                    <div
                        className="relative aspect-square bg-gray-100 cursor-pointer group"
                        onClick={() => onOpenLightbox(player)}
                    >
                        <img
                            src={player.photo_url}
                            alt={player.player_name}
                            className="w-full h-full object-cover group-hover/card:scale-105 transition-transform duration-500"
                            loading="lazy"
                        />
                        {/* Timestamp Overlay */}
                        {player.updated_at && (
                            <div className="absolute bottom-2 right-2 z-10 px-2 py-0.5 bg-black/50 backdrop-blur-sm rounded-full pointer-events-none">
                                <span className="text-[9px] font-bold text-white uppercase tracking-tighter">
                                    {formatRelativeTime(player.updated_at)}
                                </span>
                            </div>
                        )}
                        {/* Hover overlay */}
                        <div className="absolute inset-0 bg-black bg-opacity-0 group-hover/card:bg-opacity-20 transition-all flex items-center justify-center">
                            <span className="text-white opacity-0 group-hover/card:opacity-100 text-sm font-medium bg-black bg-opacity-50 px-2 py-1 rounded">
                                Click for full size
                            </span>
                        </div>
                    </div>
                ) : (
                    <div className="aspect-square bg-gray-100 flex items-center justify-center">
                        <span className="text-gray-400 text-sm">No Photo</span>
                    </div>
                )}
            </div>

            <div className="p-3">
                {/* Editable Player Name + First/Last name */}
                {isEditing ? (
                    <div className="mb-2 space-y-1.5">
                        <input
                            type="text"
                            value={editedName}
                            onChange={(e) => setEditedName(e.target.value)}
                            onKeyDown={handleKeyDown}
                            autoFocus
                            placeholder="Display name"
                            className="w-full px-2 py-1 text-sm font-medium text-gray-900 border border-blue-400 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                        <div className="flex gap-1.5">
                            <input
                                type="text"
                                value={editedFirst}
                                onChange={(e) => setEditedFirst(e.target.value)}
                                onKeyDown={handleKeyDown}
                                placeholder="First name"
                                className="w-full min-w-0 px-2 py-1 text-xs text-gray-700 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                            <input
                                type="text"
                                value={editedLast}
                                onChange={(e) => setEditedLast(e.target.value)}
                                onKeyDown={handleKeyDown}
                                placeholder="Last name"
                                className="w-full min-w-0 px-2 py-1 text-xs text-gray-700 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                        </div>
                        <div className="flex gap-1.5">
                            <button
                                onClick={handleSaveName}
                                className="flex-1 px-2 py-1 text-xs font-semibold text-white bg-blue-600 rounded hover:bg-blue-700"
                            >
                                Save
                            </button>
                            <button
                                onClick={cancelEdit}
                                className="flex-1 px-2 py-1 text-xs font-medium text-gray-600 bg-gray-100 rounded hover:bg-gray-200"
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                ) : (
                    <div className="mb-2">
                        <div className="flex items-center gap-1">
                            <p
                                className="font-medium text-gray-900 truncate flex-1 cursor-pointer hover:text-blue-600"
                                onClick={openEdit}
                                title="Click to edit details"
                            >
                                <HighlightedText text={player.player_name} highlight={searchQuery} />
                            </p>
                            <button
                                onClick={openEdit}
                                className="p-1 text-gray-400 hover:text-blue-600 rounded transition-colors"
                                title="Edit details"
                            >
                                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                                </svg>
                            </button>
                        </div>
                        {(player.first_name || player.last_name) && (
                            <p className="text-[11px] text-gray-500 truncate" title="First / Last name">
                                {[player.first_name, player.last_name].filter(Boolean).join(' ')}
                            </p>
                        )}
                        {/* Confirmed badge — visible to everyone; only super_admins can change it */}
                        {player.confirmed && (
                            <span
                                className="mt-1 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-green-100 text-green-700"
                                title="Confirmed for the final list — accreditation badge can be printed"
                            >
                                <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                </svg>
                                Confirmed
                            </span>
                        )}
                    </div>
                )}

                {/* Primary Action buttons - clean layout */}
                <div className="flex gap-1 items-center md:opacity-0 md:translate-y-2 group-hover/card:opacity-100 group-hover/card:translate-y-0 transition-all duration-300 focus-within:opacity-100 focus-within:translate-y-0">
                    {/* Link to Sportradar (if not linked) */}
                    {!player.person_id && (
                        <button
                            onClick={() => onOpenLink(player)}
                            className="flex-1 p-2 text-purple-600 hover:bg-purple-50 rounded-lg transition-colors active:scale-95"
                            title="Link to Sportradar"
                        >
                            <svg className="w-5 h-5 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                            </svg>
                        </button>
                    )}

                    {/* Upload Photo */}
                    <label className="flex-1 p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors cursor-pointer active:scale-95" title="Upload Photo">
                        <svg className="w-5 h-5 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                        </svg>
                        <input
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={(e) => onReplacePhoto(e, player)}
                        />
                    </label>

                    {/* Download (if photo exists) */}
                    {player.upload_status && player.photo_url && (
                        <button
                            onClick={() => onDownload(player)}
                            className="flex-1 p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors active:scale-95"
                            title="Download"
                        >
                            <svg className="w-5 h-5 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                            </svg>
                        </button>
                    )}

                    {/* Sync to Sportradar (linked players with photo) */}
                    {player.person_id && player.upload_status && player.photo_url && (
                        <button
                            onClick={() => onSyncToSportradar(player)}
                            className="flex-1 p-2 text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors active:scale-95"
                            title="Sync Photo to Sportradar"
                        >
                            <svg className="w-5 h-5 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
                            </svg>
                        </button>
                    )}

                    {/* 3-Dot Overflow Menu */}
                    <div className="relative" ref={menuRef}>
                        <button
                            onClick={() => setMenuOpen(!menuOpen)}
                            className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg transition-colors active:scale-95"
                            title="More actions"
                        >
                            <svg className="w-5 h-5 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
                            </svg>
                        </button>

                        {/* Dropdown Menu */}
                        {menuOpen && (
                            <div className="absolute right-0 bottom-full mb-1 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-20 max-h-64 overflow-y-auto">
                                {/* Confirm / Unconfirm (super_admin only) */}
                                {isSuperAdmin && onToggleConfirmed && (
                                    <>
                                        <button
                                            onClick={() => handleMenuAction(() => onToggleConfirmed(player))}
                                            className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                                        >
                                            <svg className="w-4 h-4 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                            </svg>
                                            {player.confirmed ? 'Remove Confirmation' : 'Mark as Confirmed'}
                                        </button>
                                        <div className="border-t border-gray-100 my-1"></div>
                                    </>
                                )}

                                {/* View Sportradar Photo */}
                                {player.person_id && (
                                    <button
                                        onClick={() => handleMenuAction(() => onViewSportradarPhoto(player))}
                                        className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                                    >
                                        <svg className="w-4 h-4 text-cyan-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                        </svg>
                                        View Sportradar Photo
                                    </button>
                                )}

                                {/* Restore Uploaded Photo */}
                                {canRestoreUploadedPhoto && onRestorePhoto && (
                                    <button
                                        onClick={() => handleMenuAction(() => onRestorePhoto(player))}
                                        className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                                    >
                                        <svg className="w-4 h-4 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
                                        </svg>
                                        Restore Uploaded Photo
                                    </button>
                                )}

                                {/* Restore Sportradar Photo */}
                                {canRestoreSportradarPhoto && onRestoreSportradarPhoto && (
                                    <button
                                        onClick={() => handleMenuAction(() => onRestoreSportradarPhoto(player))}
                                        className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                                    >
                                        <svg className="w-4 h-4 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                        </svg>
                                        Restore Sportradar Photo
                                    </button>
                                )}

                                {/* Restore Original Photo (pre-background-removal) */}
                                {canRestoreOriginalPhoto && onRestoreOriginalPhoto && (
                                    <button
                                        onClick={() => handleMenuAction(() => onRestoreOriginalPhoto(player))}
                                        className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                                    >
                                        <svg className="w-4 h-4 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                        </svg>
                                        Restore Original Photo
                                    </button>
                                )}

                                {/* Delete Photo */}
                                {player.upload_status && player.photo_url && (
                                    <button
                                        onClick={() => handleMenuAction(() => onDeletePhoto(player))}
                                        className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                                    >
                                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                        </svg>
                                        Delete Photo
                                    </button>
                                )}

                                {/* Remove Background */}
                                {player.upload_status && player.photo_url && onRemoveBackground && (
                                    <button
                                        onClick={() => handleMenuAction(() => onRemoveBackground(player))}
                                        className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                                    >
                                        <svg className="w-4 h-4 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.384-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
                                        </svg>
                                        Remove Background
                                    </button>
                                )}

                                {/* Crop Photo */}
                                {player.upload_status && player.photo_url && onCropPhoto && (
                                    <button
                                        onClick={() => handleMenuAction(() => onCropPhoto(player))}
                                        className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                                    >
                                        <svg className="w-4 h-4 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                        </svg>
                                        Crop Photo
                                    </button>
                                )}

                                <div className="border-t border-gray-100 my-1"></div>

                                {/* Remove Player */}
                                <button
                                    onClick={() => handleMenuAction(() => onDeletePlayer(player))}
                                    className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                                >
                                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                    Remove from Roster
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>
            </div>
        </div>
    );
};

export default PlayerCard;
